import { Button, Card, CardHeader, CardBody, Link } from '@heroui/react';
import { Icon } from '@iconify/react';
import { useTranslation } from 'react-i18next';

import { useDismissable } from '@/context/DismissableContext';
import { hasAiChatbotEnabled } from '@/patient-app-common/helpers/education_helpers';
import useSegment from '@/hooks/useSegment';
import { Patient } from '@/patient-app-common/types/Patient';

const SEAMLESS_ANSWERS_TRY_IT_NOW_EVENT = 'Seamless Answers try it now clicked';
const SEAMLESS_ANSWERS_DISMISSED_EVENT = 'Seamless Answers dismissed';

export function EduChatLink({ patient }: { patient: Patient }) {
  const { t } = useTranslation('home');
  const { sendTrackEvent } = useSegment();

  const hasAiChatbot = hasAiChatbotEnabled(patient);

  const DISMISS_ID = 'seamless_answers_card';
  const { persistentDismissed, dismissAlert } = useDismissable();

  if (persistentDismissed[DISMISS_ID] || !hasAiChatbot) {
    return null;
  }

  const handleDismiss = () => {
    dismissAlert(DISMISS_ID, true);
    sendTrackEvent(SEAMLESS_ANSWERS_DISMISSED_EVENT);
  };

  return (
    <>
      <Card
        className="bg-gradient-to-br from-primary-500 to-primary-600 p-4 rounded-[12px] text-white relative overflow-hidden after:content-[''] after:absolute after:-right-[30px] after:-top-[30px] after:w-[120px] after:h-[120px] after:bg-[rgba(255,255,255,0.1)] after:rounded-full after:pointer-events-none"
        radius="sm"
        shadow="sm"
      >
        <CardHeader className="flex flex-row items-center justify-between gap-2 p-1 pt-0">
          <h3 className="text-xl font-[900]">{t('common.link.answers')}</h3>
          <span className="inline-block bg-warning-200 text-warning-900 text-[10px] font-[900] p-2 pt-1 pb-1 rounded-[4px] uppercase">
            {t('common.text.new')}
          </span>
          <div className="ml-auto flex">
            <Button
              className="bg-[rgba(255,255,255,0.2)] w-7 h-7 p-0 min-w-0 min-h-0 border-none text-white rounded-full flex items-center justify-center cursor-pointer relative"
              color="primary"
              onPress={handleDismiss}
              aria-label={t('common.action.close')}
            >
              <Icon icon="mdi:close" className="text-white text-medium" />
            </Button>
          </div>
        </CardHeader>
        <CardBody className="flex flex-row items-center justify-between gap-5 p-1">
          <p className="text-small">{t('educhatlink.description')}</p>
          <div className="flex ml-auto">
            <Button
              as={Link}
              href={`/education`}
              className="bg-white w-28 h-9 text-primary-600 border-0 px-5 py-3 rounded-lg text-xs font-[900] cursor-pointer inline-flex items-center gap-2"
              color="primary"
              onPress={() => sendTrackEvent(SEAMLESS_ANSWERS_TRY_IT_NOW_EVENT)}
            >
              {t('educhatlink.button')}
              <Icon
                icon="mdi:arrow-right"
                className="text-primary-600 text-xs"
                aria-hidden="true"
              />
            </Button>
          </div>
        </CardBody>
      </Card>
    </>
  );
}
